import { useCallback, useState } from 'react';
import { Button, Card, EmptyState, PageHeader, Spinner, StatusPill } from '../components/ui';
import { Pagination } from '../components/Pagination';
import { Dialog } from '../components/dialogs/Dialog';
import { PayoutsAPI } from '../services/endpoints/admin';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { useDispatchAction } from '../hooks/useDispatchAction';
import type { Page, Payout, PayoutStatus } from '../types/api';
import { formatNaira, formatDate, formatDateTime } from '../utils/format';
import { NotImplementedBanner } from './UsersPage';
import './common.css';
import './MediaDetailPage.css';

const FILTERS: { key: PayoutStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'PENDING', label: 'Pending' },
  { key: 'PAID', label: 'Paid' },
  { key: 'HELD', label: 'Held' },
  { key: 'NO_BANK', label: 'No bank' },
  { key: 'FAILED', label: 'Failed' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'CANCELLED', label: 'Cancelled' },
];

const naira = (kobo: number) => formatNaira((kobo ?? 0) / 100);

interface Filters { status: PayoutStatus | 'all' }

export function PayoutsPage() {
  const [detail, setDetail] = useState<Payout | null>(null);
  const { run, busy } = useDispatchAction();

  const fetcher = useCallback(async ({ page, size, filters }: { page: number; size: number; filters: Filters }): Promise<Page<Payout>> => {
    return PayoutsAPI.list(filters.status === 'all' ? undefined : filters.status, page, size);
  }, []);
  const q = usePagedQuery<Payout, Filters>({ fetcher, initialFilters: { status: 'PENDING' }, pageSize: 25 });

  const approve = async (p: Payout) => {
    const updated = await run(`Approve payout to ${p.creatorName || p.creatorId}`, () => PayoutsAPI.approve(p.id));
    if (updated) { q.refresh(); setDetail((d) => (d?.id === p.id ? updated : d)); }
  };
  const reject = async (p: Payout) => {
    const updated = await run('Reject payout', () => PayoutsAPI.reject(p.id));
    if (updated) { q.refresh(); setDetail((d) => (d?.id === p.id ? updated : d)); }
  };
  const runDaily = async () => {
    const n = await run("Stage yesterday's payouts", () => PayoutsAPI.runNow());
    if (n !== null) q.refresh();
  };

  return (
    <div>
      <PageHeader
        eyebrow="16 — PAYOUTS"
        title={<>The <em>ledger</em>.</>}
        lede="Daily creator payouts. The scheduler stages each creator's net (after the platform fee) every morning for the previous day; approving one fires the Paystack transfer. Funds stay in the platform account until then."
        actions={<Button tone="ghost" disabled={busy} onClick={runDaily}>Stage yesterday now</Button>}
      />

      {q.error && <NotImplementedBanner endpoint="/api/admin/payouts" />}

      <div className="studio-chips" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '0 0 16px' }}>
        {FILTERS.map((f) => (
          <Button key={f.key} size="sm" tone={q.filters.status === f.key ? 'ember' : 'ghost'} onClick={() => q.setFilters({ status: f.key })}>{f.label}</Button>
        ))}
      </div>

      {q.loading && !q.data.length ? <Card><Spinner /> Loading…</Card> :
       q.data.length === 0 ? <Card><EmptyState title="No payouts" hint="Staged payouts will appear here after the daily run." /></Card> :
        <Card padded={false}>
          <table className="nb-table">
            <thead>
              <tr><th>Creator</th><th>Day</th><th>Gross</th><th>Fee</th><th>Net</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {q.data.map((p) => (
                <tr key={p.id} className="nb-table__row-tap" onClick={() => setDetail(p)}>
                  <td>
                    <div className="nb-table__primary">{p.creatorName || '—'}</div>
                    <div className="nb-table__sub">{p.creatorId}</div>
                  </td>
                  <td className="nb-table__mono">{p.periodKey || formatDate(p.periodStart)}</td>
                  <td className="nb-table__mono">{naira(p.grossKobo)}</td>
                  <td className="nb-table__mono">{naira(p.feeKobo)}</td>
                  <td className="nb-table__mono">{naira(p.netKobo)}</td>
                  <td><PayoutPill status={p.status} />{p.failureReason && <div className="nb-table__sub">{p.failureReason}</div>}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {p.status === 'PENDING' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button size="sm" tone="ember" disabled={busy} onClick={() => approve(p)}>Approve</Button>
                        <Button size="sm" tone="ghost" disabled={busy} onClick={() => reject(p)}>Reject</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={q.page} pageSize={q.pageSize} total={q.totalCount} onPage={q.setPage} />
        </Card>
      }

      <Dialog open={!!detail} onClose={() => setDetail(null)} title="Payout details"
        footer={detail?.status === 'PENDING'
          ? <div style={{ display: 'flex', gap: 8 }}>
              <Button tone="ember" disabled={busy} onClick={() => detail && approve(detail)}>Approve &amp; transfer</Button>
              <Button tone="ghost" disabled={busy} onClick={() => detail && reject(detail)}>Reject</Button>
            </div>
          : undefined}>
        {detail && (
          <dl className="md-static">
            <Row label="Creator" value={detail.creatorName || detail.creatorId} />
            <Row label="Creator ID" value={<code className="md-mono">{detail.creatorId}</code>} />
            <Row label="Day" value={detail.periodKey || `${formatDate(detail.periodStart)} → ${formatDate(detail.periodEnd)}`} />
            <Row label="Paid by students" value={naira(detail.grossKobo)} />
            <Row label="Platform fee" value={naira(detail.feeKobo)} />
            <Row label="Net payout" value={naira(detail.netKobo)} />
            <Row label="Enrolments" value={detail.liveEpisodesCount
              ? `${detail.enrollmentsCount ?? 0} · across ${detail.liveEpisodesCount} live episode${detail.liveEpisodesCount === 1 ? '' : 's'}`
              : (detail.enrollmentsCount ?? '—')} />
            <Row label="Status" value={<PayoutPill status={detail.status} />} />
            <Row label="Recipient" value={detail.recipientCode ? <code className="md-mono">{detail.recipientCode}</code> : '—'} />
            <Row label="Transfer code" value={detail.transferCode ? <code className="md-mono">{detail.transferCode}</code> : '—'} />
            <Row label="Reference" value={detail.reference ? <code className="md-mono">{detail.reference}</code> : '—'} />
            <Row label="Approved by" value={detail.approvedBy || '—'} />
            <Row label="Paid at" value={detail.paidAt ? formatDateTime(detail.paidAt) : '—'} />
            {detail.failureReason && <Row label="Failure" value={detail.failureReason} />}
          </dl>
        )}
      </Dialog>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return <div className="md-row"><dt>{label}</dt><dd>{value}</dd></div>;
}

function PayoutPill({ status }: { status: PayoutStatus }) {
  if (status === 'PAID')      return <StatusPill tone="moss">Paid</StatusPill>;
  if (status === 'PENDING')   return <StatusPill tone="brass">Pending</StatusPill>;
  if (status === 'HELD')      return <StatusPill tone="brass">Held</StatusPill>;
  if (status === 'NO_BANK')   return <StatusPill tone="rust">No bank</StatusPill>;
  if (status === 'REJECTED')  return <StatusPill tone="rust">Rejected</StatusPill>;
  if (status === 'CANCELLED') return <StatusPill tone="rust">Cancelled</StatusPill>;
  return <StatusPill tone="ember">Failed</StatusPill>;
}
